import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { get, over, set } from "@totalsoft/change-tracking-react";
import { emptyArray } from "constants/defaultValues";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import {
  AREA_OF_ACTIVITIES_QUERY,
  CITIES_QUERY,
  GENDER_VALUES_QUERY,
  LEGAL_FORM_VALUES_QUERY,
  USER_DATA,
} from "features/common/queries";
import { legalForm, gender } from "constants/queryTypes";
import { initialAddress, initialPerson } from "features/client/constants";
import { useApolloClient } from "@apollo/client";
import { cityTypes, countryCodeConstants, legalFormCodes } from "features/common/constants";
import { getTenant } from "providers/TenantProvider";
import ClientSoleTraderFeature from "./ClientSoleTraderFeature";
import PersonList from "./PersonList";
import { Button, Grid } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { remove } from "ramda";

function ClientSoleTraderFeatureContainer({ selectedClientLens, isSoleTraderFeature, validation, onBack }) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { countryCode } = getTenant();

  const [legalFormValues, setLegalFormValues] = useState(emptyArray);
  const [genderValues, setGenderValues] = useState(emptyArray);
  const [countries, setCountries] = useState(emptyArray);
  const [counties, setCounties] = useState(emptyArray);
  const [areaOfActivities, setAreaOfActivities] = useState(emptyArray);

  const selectedClient = selectedClientLens |> get;

  const newPerson = useMemo(() => {
    return {
      ...initialPerson,
      firstName: selectedClient.firstName,
      lastName: selectedClient.lastName,
      birthDate: selectedClient.birthDate,
      email: selectedClient.email,
      pin: selectedClient.pin,
      nationality:
        countryCode === countryCodeConstants.unitedKingdom
          ? t("Person.Nationalities.UnitedKingdom")
          : t("Person.Nationalities.Ireland"),
      officialPartnerAddress: initialAddress,
    };
  }, [countryCode, selectedClient, t]);

  useEffect(() => {
    if (!isSoleTraderFeature) return;
    if (!Object.keys(selectedClient).includes("persons") || !selectedClient.persons[0].officialPartnerAddress) {
      (selectedClientLens |> set)({
        ...selectedClient,
        persons: [newPerson],
      });
    }
  }, [isSoleTraderFeature, selectedClientLens, selectedClient, newPerson]);

  const { loading: loadingCountries } = useQueryWithErrorHandling(CITIES_QUERY, {
    variables: { cityTypeId: cityTypes.country },
    onCompleted: (data) => {
      setCountries(data.cities);
      client.watchQuery({ query: USER_DATA, fetchPolicy: "cache-only" }).subscribe({
        next: ({ data }) => {
          (selectedClientLens.countryId |> set)(data?.userData?.countryId);
        },
      });
    },
  });

  const { loading: loadingLegalFormValues } = useQueryWithErrorHandling(LEGAL_FORM_VALUES_QUERY, {
    variables: { type: legalForm },
    skip: isSoleTraderFeature,
    onCompleted: (data) => {
      setLegalFormValues(data.legalFormValues.filter((l) => l.code !== legalFormCodes.soleTrader));
    },
  });

  const { loading: loadingGenderValues } = useQueryWithErrorHandling(GENDER_VALUES_QUERY, {
    variables: { type: gender },
    onCompleted: (data) => {
      setGenderValues(data.genderValues);
    },
  });

  const { loading: loadingCounties } = useQueryWithErrorHandling(CITIES_QUERY, {
    variables: { cityTypeId: cityTypes.county, parentCityId: selectedClient.countryId },
    skip: !selectedClient.countryId,
    onCompleted: (data) => {
      setCounties(data.cities);
    },
  });

  const { loading: loadingAreaOfActivities } = useQueryWithErrorHandling(AREA_OF_ACTIVITIES_QUERY, {
    skip: isSoleTraderFeature,
    onCompleted: (data) => {
      setAreaOfActivities(data.areaOfActivities);
    },
  });

  const handleAdd = useCallback(() => {
    (selectedClientLens.persons |> set)([...selectedClient.persons, newPerson]);
  }, [selectedClientLens, selectedClient, newPerson]);

  const handleDelete = useCallback(
    (index) => {
      if (selectedClient.persons.length === 2) return;
      over(selectedClientLens.persons, remove(index, 1));
    },
    [selectedClientLens.persons, selectedClient]
  );

  if (loadingCountries || loadingLegalFormValues || loadingCounties || loadingGenderValues || loadingAreaOfActivities) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <>
      {!isSoleTraderFeature && (
        <ClientSoleTraderFeature
          selectedClientLens={selectedClientLens}
          legalFormValues={legalFormValues}
          areaOfActivities={areaOfActivities}
          countries={countries}
          counties={counties}
          validation={validation}
          onBack={onBack}
        />
      )}
      <PersonList
        partnerLens={selectedClientLens}
        isSoleTraderFeature={isSoleTraderFeature}
        genderValues={genderValues}
        countries={countries}
        counties={counties}
        onRemove={handleDelete}
        onBack={onBack}
        validation={validation.persons}
      />
      {!isSoleTraderFeature && Object.keys(selectedClient).includes("persons") && (
        <Grid item container justifyContent="flex-end">
          <Button
            id="createClient_AddPersonBtn"
            right="true"
            variant="contained"
            color="primary"
            onClick={handleAdd}
            endIcon={<Add />}
            disabled={selectedClient.persons?.length === 4}
          >
            {t("Client.AddPerson")}
          </Button>
        </Grid>
      )}
    </>
  );
}

ClientSoleTraderFeatureContainer.propTypes = {
  selectedClientLens: PropTypes.object.isRequired,
  isSoleTraderFeature: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default ClientSoleTraderFeatureContainer;
